// Human-facing numbers: the patient number on a card, the invoice number on a receipt,
// the sample number on a lab tube.
//
// These are not the internal IDs. Internal IDs are UUIDv7 and never appear in a URL
// (CLAUDE.md, domain rule 8). Human numbers are short, per-facility and formatted the way
// a particular facility already numbers things, because reception and the lab will keep
// their old registers side by side with Wodi for a while and the numbers have to match.
//
// This module is only the formatting and the reading back. Issuing the next sequence
// value — the counter, its per-facility scope and the locking that stops two clerks
// getting the same number — is a database concern and lives in packages/db.
//
// Templates look like `P-{seq:6}` or `{facility}/{yy}/{seq:5}`.

import type { Result } from './result.ts';
import { err, ok } from './result.ts';
import type { CalendarDay } from './time.ts';
import { daysInMonth, isValidDay } from './time.ts';

export type HumanNumberErrorCode =
  | 'human_number.empty_format'
  | 'human_number.malformed_placeholder'
  | 'human_number.unknown_placeholder'
  | 'human_number.invalid_width'
  | 'human_number.missing_sequence'
  | 'human_number.repeated_sequence'
  | 'human_number.ambiguous_format'
  | 'human_number.invalid_sequence'
  | 'human_number.missing_facility_code'
  | 'human_number.invalid_facility_code'
  | 'human_number.missing_day'
  | 'human_number.invalid_day'
  | 'human_number.does_not_match';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const HUMAN_NUMBER_ERRORS: Record<HumanNumberErrorCode, string> = {
  'human_number.empty_format': 'A number format cannot be blank',
  'human_number.malformed_placeholder': 'A { is not closed, or a } stands on its own',
  'human_number.unknown_placeholder': 'That placeholder is not one this format understands',
  'human_number.invalid_width': 'A sequence width is a whole number of digits, 1 to 20',
  'human_number.missing_sequence': 'A number format has to include {seq}',
  'human_number.repeated_sequence': 'A number format includes {seq} once',
  'human_number.ambiguous_format': 'A number in this format could not be read back',
  'human_number.invalid_sequence': 'A sequence value is a whole number, zero or above',
  'human_number.missing_facility_code': 'This format needs a facility code',
  'human_number.invalid_facility_code': 'A facility code is letters and digits only',
  'human_number.missing_day': 'This format needs a date',
  'human_number.invalid_day': 'That date does not exist',
  'human_number.does_not_match': 'That is not a number in this format',
};

/** The widest sequence a format may pad to. */
const MAX_SEQUENCE_WIDTH = 20;

/**
 * What a facility code may contain, and therefore what `{facility}` matches when read back.
 *
 * Letters and digits only. A hyphen would be legal in a code but would make `{facility}-`
 * unreadable, and a hyphen is far more useful as the separator in `{facility}-{seq:5}`
 * than inside the code itself.
 */
const FACILITY_CODE = /^[A-Za-z0-9]+$/;
const FACILITY_CODE_CHARACTER = /[A-Za-z0-9]/;

export type FormatPart =
  | { readonly kind: 'literal'; readonly text: string }
  | { readonly kind: 'sequence'; readonly width: number }
  | { readonly kind: 'facility' }
  | { readonly kind: 'year'; readonly digits: 2 | 4 }
  | { readonly kind: 'month' }
  | { readonly kind: 'day' };

/** A template that has been checked once so it can be used many times. */
export interface NumberFormat {
  readonly template: string;
  readonly parts: readonly FormatPart[];
}

export interface HumanNumberContext {
  /** The value the database issued. The counter itself is not this module's business. */
  readonly sequence: bigint | number;
  readonly facilityCode?: string;
  /** The EAT calendar day the number is issued on, for `{yy}`, `{yyyy}`, `{mm}` and `{dd}`. */
  readonly day?: CalendarDay;
}

/** The pieces read back out of a formatted number. Only what the format contains appears. */
export interface HumanNumberFields {
  readonly sequence: bigint;
  readonly facilityCode?: string;
  readonly year?: number;
  readonly month?: number;
  readonly day?: number;
}

// --- Compiling a template -------------------------------------------------------------

function placeholderPart(name: string): FormatPart | HumanNumberErrorCode {
  if (name === 'facility') return { kind: 'facility' };
  if (name === 'yy') return { kind: 'year', digits: 2 };
  if (name === 'yyyy') return { kind: 'year', digits: 4 };
  if (name === 'mm') return { kind: 'month' };
  if (name === 'dd') return { kind: 'day' };
  if (name === 'seq') return { kind: 'sequence', width: 1 };

  const widthMatch = /^seq:(.*)$/.exec(name);
  if (widthMatch) {
    const digits = widthMatch[1] ?? '';
    if (!/^\d+$/.test(digits)) return 'human_number.invalid_width';
    const width = Number(digits);
    if (width < 1 || width > MAX_SEQUENCE_WIDTH) return 'human_number.invalid_width';
    return { kind: 'sequence', width };
  }
  return 'human_number.unknown_placeholder';
}

/**
 * Checks a template and breaks it into parts. `{{` and `}}` are literal braces; everything
 * else between braces is a placeholder.
 *
 * Compile once — when the facility's settings are loaded — and reuse; compiling on every
 * number would re-run the validation for no reason.
 */
export function compileNumberFormat(template: string): Result<NumberFormat, HumanNumberErrorCode> {
  if (template.trim() === '') return err('human_number.empty_format');

  const parts: FormatPart[] = [];
  let literal = '';
  let index = 0;

  const flushLiteral = () => {
    if (literal !== '') {
      parts.push({ kind: 'literal', text: literal });
      literal = '';
    }
  };

  while (index < template.length) {
    const char = template[index] ?? '';
    const next = template[index + 1];

    if (char === '{' && next === '{') {
      literal += '{';
      index += 2;
      continue;
    }
    if (char === '}' && next === '}') {
      literal += '}';
      index += 2;
      continue;
    }
    if (char === '}') return err('human_number.malformed_placeholder');
    if (char === '{') {
      const close = template.indexOf('}', index + 1);
      if (close === -1) return err('human_number.malformed_placeholder');
      const name = template.slice(index + 1, close);
      const part = placeholderPart(name);
      if (typeof part === 'string') return err(part);
      flushLiteral();
      parts.push(part);
      index = close + 1;
      continue;
    }
    literal += char;
    index += 1;
  }
  flushLiteral();

  const sequences = parts.filter((part) => part.kind === 'sequence').length;
  if (sequences === 0) return err('human_number.missing_sequence');
  if (sequences > 1) return err('human_number.repeated_sequence');

  const ambiguity = findAmbiguity(parts);
  if (ambiguity) return err(ambiguity);

  return ok({ template, parts });
}

/**
 * `{seq}` and `{facility}` have no fixed length, so whatever follows one has to start with
 * a character that part cannot itself consume. `{facility}/{seq}` reads back cleanly;
 * `{facility}{seq}` and `{seq}{yy}` do not, and a number that cannot be read back cannot
 * be scanned off a card or typed into a search box.
 */
function findAmbiguity(parts: readonly FormatPart[]): HumanNumberErrorCode | null {
  for (const [index, part] of parts.entries()) {
    if (part.kind !== 'sequence' && part.kind !== 'facility') continue;
    const following = parts[index + 1];
    if (following === undefined) continue;
    if (following.kind !== 'literal') return 'human_number.ambiguous_format';
    const firstCharacter = following.text.slice(0, 1);
    const swallowed =
      part.kind === 'sequence'
        ? /\d/.test(firstCharacter)
        : FACILITY_CODE_CHARACTER.test(firstCharacter);
    if (swallowed) return 'human_number.ambiguous_format';
  }
  return null;
}

// --- Formatting -------------------------------------------------------------------------

/**
 * Renders one number. The sequence width is a minimum, not a cap: a facility that issues
 * more numbers than its format was padded for keeps getting valid, longer numbers instead
 * of colliding ones.
 */
export function formatHumanNumber(
  format: NumberFormat,
  context: HumanNumberContext,
): Result<string, HumanNumberErrorCode> {
  const sequence = toSequence(context.sequence);
  if (sequence === null) return err('human_number.invalid_sequence');

  let out = '';
  for (const part of format.parts) {
    switch (part.kind) {
      case 'literal':
        out += part.text;
        break;
      case 'sequence':
        out += sequence.toString().padStart(part.width, '0');
        break;
      case 'facility': {
        const code = context.facilityCode;
        if (code === undefined || code === '') return err('human_number.missing_facility_code');
        if (!FACILITY_CODE.test(code)) return err('human_number.invalid_facility_code');
        out += code;
        break;
      }
      case 'year':
      case 'month':
      case 'day': {
        const day = context.day;
        if (day === undefined) return err('human_number.missing_day');
        if (!isValidDay(day)) return err('human_number.invalid_day');
        out += datePart(part, day);
        break;
      }
    }
  }
  return ok(out);
}

function datePart(
  part: Extract<FormatPart, { kind: 'year' | 'month' | 'day' }>,
  day: CalendarDay,
): string {
  if (part.kind === 'month') return String(day.month).padStart(2, '0');
  if (part.kind === 'day') return String(day.day).padStart(2, '0');
  return part.digits === 2
    ? String(day.year % 100).padStart(2, '0')
    : String(day.year).padStart(4, '0');
}

function toSequence(value: bigint | number): bigint | null {
  if (typeof value === 'bigint') return value < 0n ? null : value;
  if (!Number.isSafeInteger(value) || value < 0) return null;
  return BigInt(value);
}

// --- Reading a number back ---------------------------------------------------------------

/**
 * Reads a number in a known format back into its pieces, so a scanned card or a typed
 * invoice number can be turned into a lookup.
 *
 * Literals are matched exactly, including case: a printed number is reproduced exactly by
 * a scanner, and two facilities' formats that differ only in case are two different
 * formats. `{yy}` is read as 20YY — a two-digit year cannot express anything else, and a
 * facility that needs a longer horizon uses `{yyyy}`.
 */
export function parseHumanNumber(
  format: NumberFormat,
  value: string,
): Result<HumanNumberFields, HumanNumberErrorCode> {
  const order: FormatPart[] = [];
  let pattern = '';
  for (const part of format.parts) {
    switch (part.kind) {
      case 'literal':
        pattern += escapeForRegExp(part.text);
        continue;
      case 'sequence':
        pattern += `(\\d{${String(part.width)},})`;
        break;
      case 'facility':
        pattern += '([A-Za-z0-9]+)';
        break;
      case 'year':
        pattern += part.digits === 2 ? '(\\d{2})' : '(\\d{4})';
        break;
      case 'month':
      case 'day':
        pattern += '(\\d{2})';
        break;
    }
    order.push(part);
  }

  const match = new RegExp(`^${pattern}$`).exec(value.trim());
  if (!match) return err('human_number.does_not_match');

  let sequence = 0n;
  let facilityCode: string | undefined;
  let year: number | undefined;
  let month: number | undefined;
  let dayOfMonth: number | undefined;

  // A placeholder may appear more than once (`{yy}` in two places, say); every
  // occurrence was rendered from the same context, so they have to agree.
  const disagrees = <T>(previous: T | undefined, next: T): boolean =>
    previous !== undefined && previous !== next;

  for (const [index, part] of order.entries()) {
    const captured = match[index + 1] ?? '';
    switch (part.kind) {
      case 'sequence':
        sequence = BigInt(captured);
        break;
      case 'facility':
        if (disagrees(facilityCode, captured)) return err('human_number.does_not_match');
        facilityCode = captured;
        break;
      case 'year': {
        const value = part.digits === 2 ? 2000 + Number(captured) : Number(captured);
        if (disagrees(year, value)) return err('human_number.does_not_match');
        year = value;
        break;
      }
      case 'month':
        if (disagrees(month, Number(captured))) return err('human_number.does_not_match');
        month = Number(captured);
        break;
      case 'day':
        if (disagrees(dayOfMonth, Number(captured))) return err('human_number.does_not_match');
        dayOfMonth = Number(captured);
        break;
      case 'literal':
        break;
    }
  }

  if (month !== undefined && (month < 1 || month > 12)) return err('human_number.does_not_match');
  if (dayOfMonth !== undefined) {
    // Without a year, allow 29 February; without a month, any day up to 31.
    const longest = month === undefined ? 31 : daysInMonth(year ?? 2000, month);
    if (dayOfMonth < 1 || dayOfMonth > longest) return err('human_number.does_not_match');
  }

  const fields: {
    sequence: bigint;
    facilityCode?: string;
    year?: number;
    month?: number;
    day?: number;
  } = { sequence };
  if (facilityCode !== undefined) fields.facilityCode = facilityCode;
  if (year !== undefined) fields.year = year;
  if (month !== undefined) fields.month = month;
  if (dayOfMonth !== undefined) fields.day = dayOfMonth;
  return ok(fields);
}

function escapeForRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
